/* eslint-disable complexity */
/* eslint-disable max-statements */
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useMemoMergePropsWithDefault } from '@elliemae/ds-props-helpers';
import { DSTabsDefaultProps } from '../react-desc-prop-types.js';
import type { DSTabsT, DSTabT } from '../react-desc-prop-types.js';
import type { DSTabsInternalsT } from '../sharedTypes.js';
import { centerTab, shouldHaveLeftGradient, shouldHaveRightGradient } from '../utils/helpers.js';

export const useTabs = (props: DSTabsT.Props): DSTabsInternalsT.DSTabsUseTabsContextT => {
  const propsWithDefaults = useMemoMergePropsWithDefault<DSTabsT.InternalProps>(props, DSTabsDefaultProps);

  const { activeTab, children, onTabChange, isDSMobile, fixedTabsHeaders } = propsWithDefaults;

  const tabsRef = useRef<Record<number, HTMLButtonElement>>({});
  const tabsRefAsArray = useRef<HTMLButtonElement[]>([]);
  const focusableTabsRef = useRef<HTMLButtonElement[]>([]);
  const carouselOnlyListRef = useRef<HTMLDivElement | null>(null);
  const tabsListRef = useRef<HTMLDivElement | null>(null);
  const actualActiveTabRef = useRef<HTMLButtonElement | null>(null);

  // we can't activate disabled tabs, we search for the first activable tab
  const firstActivableTabId = useMemo(() => {
    const childrenArray = React.Children.toArray(children) as React.ReactElement<DSTabT.Props>[];
    const firstActivableTab = childrenArray.filter((tab) => tab.props.disabled !== true)[0];

    if (!firstActivableTab) return '';
    return firstActivableTab.props.tabId ?? '';
  }, [children]);

  const [internalActiveTab, setInternalActiveTab] = useState<string>(firstActivableTabId);

  const actualActiveTab = useMemo(() => {
    if (activeTab !== undefined) return activeTab;
    return internalActiveTab;
  }, [activeTab, internalActiveTab]);

  useEffect(() => {
    actualActiveTabRef.current = focusableTabsRef.current.find((el) => el.dataset.tabId === actualActiveTab) ?? null;
  }, [actualActiveTab]);

  useEffect(() => {
    if (activeTab === undefined) {
      const firstAvailableTab = focusableTabsRef.current.find((el) => el.ariaDisabled !== 'true');
      setInternalActiveTab(firstAvailableTab?.dataset.tabId || '');
    }
  }, [activeTab]);

  const [mobileGradients, setMobileGradients] = useState<DSTabsInternalsT.MobileGradientsT>({
    left: false,
    right: false,
  });

  /** [PUI-15772] - Tabs Pre Refactor
   * Updates mobile gradient visibility. It:
   * 1. Calculates scroll position
   * 2. Updates gradient visibility based on scroll
   */
  const updateMobileGradients = React.useCallback(() => {
    if (tabsListRef.current && isDSMobile && !fixedTabsHeaders) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsListRef.current;
      setMobileGradients({
        left: shouldHaveLeftGradient(scrollLeft),
        right: shouldHaveRightGradient(scrollLeft, clientWidth, scrollWidth),
      });
    }
  }, [fixedTabsHeaders, isDSMobile, tabsListRef]);

  React.useLayoutEffect(() => {
    if (isDSMobile && !fixedTabsHeaders) {
      updateMobileGradients();
    }
  }, [updateMobileGradients, isDSMobile, fixedTabsHeaders, actualActiveTab]);

  // due to a bunch of reasons and the component being biting more than it can chew
  //  optimizing renders to make the swipe-card work "as expected"(not animating on first render)
  //  simply isn't feasible at this time.
  // how do we fix this anyway?
  //   we track the first time a tab change triggered by the user happens
  //   after the first user interaction we enable animations,
  //     after that we give the animation handling control back to the swipe-card.
  const userDidChangeTabAtleastOnceRef = useRef(false);

  const handleOnTabChange = React.useCallback(
    (tabId: string, e?: React.MouseEvent) => {
      setInternalActiveTab(tabId);
      onTabChange(tabId, e);
      userDidChangeTabAtleastOnceRef.current = true;
      if (isDSMobile && !fixedTabsHeaders && e) {
        centerTab({ e, listRef: tabsListRef });
      }
    },
    [fixedTabsHeaders, isDSMobile, onTabChange, setInternalActiveTab, tabsListRef],
  );

  const globalClickHandler = React.useCallback(
    (data: DSTabsInternalsT.GlobalClickHandlerT): void => {
      if (data.target === 'panel') {
        // we need to allow the text selection in the panel
        // this enters on conflict with the SwipeableViews component
        if (propsWithDefaults.allowTextSelection) {
          data.event.stopPropagation();
        }
      }

      if (data.target === 'tabsList') {
        const target = data.event.target as HTMLButtonElement;
        const { dataset, ariaDisabled } = target;
        if (dataset.tabId && ariaDisabled !== 'true') {
          // handleOnTabChange(dataset.tabId, data.event);
          if (isDSMobile && !fixedTabsHeaders && data.event) {
            centerTab({ e: data.event, listRef: tabsListRef });
          }
        }
      }
      if (data.target === 'swipeableViews') {
        handleOnTabChange(data.tabId!);
      }
      if (data.target === 'tabItem' && data.tabId && !(data.event.currentTarget as HTMLButtonElement).disabled) {
        handleOnTabChange(data.tabId, data.event);
      }
    },
    [propsWithDefaults.allowTextSelection, handleOnTabChange, tabsListRef, fixedTabsHeaders, isDSMobile],
  );
  const ctx = useMemo(
    () => ({
      props: propsWithDefaults,
      tabsRef,
      focusableTabsRef,
      tabsListRef,
      tabsRefAsArray,
      setInternalActiveTab,
      actualActiveTab,
      actualActiveTabRef,
      carouselOnlyListRef,
      globalClickHandler,
      mobileGradients,
      updateMobileGradients,
      handleOnTabChange,
      userDidChangeTabAtleastOnceRef,
    }),
    [
      propsWithDefaults,
      actualActiveTab,
      globalClickHandler,
      mobileGradients,
      updateMobileGradients,
      handleOnTabChange,
      userDidChangeTabAtleastOnceRef,
    ],
  );

  return ctx;
};
